import { SkeletonWoo } from '@/components/skeleton/skeleton.woo';
import { useGetAccountServices, useGetDevices, useGetProfiles } from '@/hooks/api';
import { useGetMatchedRoute } from '@/hooks/routing';
import { groupServiceAccounts } from '@/routes/(services)/-model/group-service-accounts';
import {
  WooArrowLeft,
  WooChevronRight,
  WooDeviceGroup,
  TvAvatarRoundWithBorder,
} from '@nosinovacao/fe-icons';
import { Button, cn, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link } from '@tanstack/react-router';

export function TvNavSheetContentWoo({
  goToMainSection,
  onClose,
  backButton = true,
  className,
}: Readonly<{
  onClose: () => void;
  goToMainSection: () => void;
  backButton?: boolean;
  className?: string;
}>) {
  const { t } = useI18n();
  const { data, isLoading } = useGetAccountServices();
  return (
    <div
      className={cn(
        '-mb-6 flex h-full flex-col items-start gap-3 bg-nos-neutral-0 px-4 pb-6 pt-3',
        className,
      )}
    >
      {backButton ? (
        <Button skin="woo" variant="tertiary" onClick={goToMainSection} data-tid="button-back">
          <WooArrowLeft width={20} height={20} /> {t('Common.Back')}
        </Button>
      ) : null}

      <span className="font-light text-nos-neutral-900 font-title-h3" data-tid="title-tv-selector">
        {t('Common.Navbar.TvSelector.Title')}
      </span>
      <ul className="grid w-full gap-4 overflow-y-auto">
        <SkeletonWoo
          loading={isLoading}
          className="h-32 w-full rounded-lg border border-nos-neutral-200"
          count={2}
        >
          <div className="grid gap-4">
            {groupServiceAccounts(data?.Holder?.ServiceAccounts ?? [])
              .filter((sa) => sa.HasTvService)
              .map((sa) => (
                <li key={sa.Id}>
                  <ServiceAccountNavItemWoo
                    id={sa.Id}
                    address={sa.Address || sa.Id}
                    name={sa.Package || sa.CustomerId}
                    onSelect={onClose}
                  />
                </li>
              ))}
          </div>
        </SkeletonWoo>
      </ul>
    </div>
  );
}

function ServiceAccountNavItemWoo({
  address,
  name,
  onSelect,
  id,
}: Readonly<{
  name: string;
  id: string;
  address: string;
  onSelect: () => void;
}>) {
  const { data: profiles, isLoading: profilesLoading } = useGetProfiles(id);
  const { data: devices, isLoading: devicesLoading } = useGetDevices(id);
  const match = useGetMatchedRoute();

  const active = match.routeId === '/(tv)/tv/$id' && match.params.id === id;

  return (
    <Link
      data-active={active}
      data-tid="card-tv-service-account"
      to="/tv/$id"
      params={{ id }}
      disabled={active}
      onClick={() => {
        if (!active) onSelect();
      }}
      className="flex items-center gap-2 rounded-lg border-2 border-nos-neutral-200 bg-nos-neutral-0 p-4 hover:border-woo-green focus-visible:border-woo-green data-[active=true]:border-woo-green"
    >
      <div className="grid w-full min-w-0 gap-1">
        <span className="font-medium text-nos-neutral-900 font-text-p1">{name}</span>
        <span className="break-words font-light text-nos-neutral-500 font-text-p2">{address}</span>
        <div className="flex gap-6 pt-3">
          <div className="flex items-center gap-1">
            <WooDeviceGroup width={16} height={16} className="text-nos-neutral-500" />
            <SkeletonWoo loading={devicesLoading} className="size-5 rounded-lg">
              <div className="grid size-5 place-items-center rounded-lg bg-nos-neutral-100">
                <span className="font-medium text-nos-neutral-900 font-text-p3">
                  {devices?.length ?? 0}
                </span>
              </div>
            </SkeletonWoo>
          </div>
          <div className="flex items-center gap-1">
            <TvAvatarRoundWithBorder className="text-nos-neutral-200" />
            <SkeletonWoo loading={profilesLoading} className="size-5 rounded-lg">
              <div className="grid size-5 place-items-center rounded-lg bg-nos-neutral-100">
                <span className="font-medium text-nos-neutral-900 font-text-p3">
                  {profiles?.length ?? 0}
                </span>
              </div>
            </SkeletonWoo>
          </div>
        </div>
      </div>
      <WooChevronRight className="ml-auto shrink-0 text-nos-neutral-900" width={24} height={24} />
    </Link>
  );
}
